/**
 * The visitor's own clock, as a composable — the caller `format.ts` and `timezone.ts`
 * were written for and did not have.
 *
 * ── WHY THIS FILE EXISTS ──────────────────────────────────────────────────
 *
 * `resolveVisitorTimeZone` and `createVisitorFormat` were built, argued and
 * unit-tested, and neither shipped a byte: nothing imported them.
 *
 * WHAT THEY ADD IS NOT THE ZONE. `vendor/i18n` once passed
 * `browserTimeZone()` to the shared provider, so the old `useFormat()` was rendering in
 * the visitor's own zone all along and `Transcript`'s comment saying so was
 * true. Getting that wrong in this header would have been an easy and confident
 * mistake, so it is stated plainly: the zone was never broken.
 *
 * The two things that WERE missing, and both are visible on the one screen a
 * visitor uses to work out whether anybody answered them:
 *
 *   THE DATE. `format.time()` renders a time and only a time, so a reply from
 *   three days ago reads "17:05" — a number that looks like today. `timestamp()`
 *   is the transcript rule: today is a time, anything older is a date AND a
 *   time, where "today" is the calendar day in the rendering zone rather than
 *   "within 24 hours".
 *
 *   THE ADMISSION. `browserTimeZone()` falls back to UTC when the runtime has no
 *   zone, and nothing downstream could tell the difference — so a visitor in
 *   Tallinn whose browser withheld its zone read every timestamp two hours off
 *   with nothing to warn them. `VisitorTimeZone` carries `source`, and
 *   `createVisitorFormat` qualifies every rendering with its offset when the
 *   zone is not the visitor's own. An unqualified wrong time is worse than a
 *   qualified one, because only the second can be corrected by the reader.
 *
 * ── WHAT IT DOES NOT DO ───────────────────────────────────────────────────
 *
 * It does not ask the visitor anything and it stores nothing. `Intl.DateTime
 * Format().resolvedOptions().timeZone` is a read of the device's own setting —
 * the same one every clock on it uses — and it is used here to RENDER and
 * nowhere else. Whether that value may also be SENT is a separate question with
 * a separate answer, and it lives in `privacy/visitor-context.ts` under the
 * `necessary` disclosure rather than being decided by this composable.
 *
 * ── WHY THE ZONE IS READ ONCE ─────────────────────────────────────────────
 *
 * A device time zone changes when somebody boards a plane or crosses a border,
 * which is not a thing that happens between two renders of a chat panel. Reading
 * it per render would re-run an `Intl` construction on every keystroke in the
 * composer for a value that cannot have changed, and re-reading it on an
 * interval would move every timestamp in the transcript mid-conversation.
 */
import { computed, type ComputedRef } from 'vue';

import { useLocale } from '../vendor/i18n';

import { createVisitorFormat, type VisitorFormat } from './format';
import { visitorTimeZone } from './timezone';

export function useVisitorFormat(): ComputedRef<VisitorFormat> {
  const locale = useLocale();
  // Read once per component instance, outside the computed — see the header.
  const zone = visitorTimeZone();
  return computed(() => createVisitorFormat({ locale: locale.value, zone }));
}
